import styled from 'styled-components';

export const PageWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  /* safe: when the form is taller than the window, align to the start
     so the logo stays reachable. Plain center clips the top with no scroll. */
  justify-content: center;
  justify-content: safe center;
  flex: 1 0 auto;
  box-sizing: border-box;
  background-color: var(--color-layout-bg, #eaf6f7);
  padding-top: 30px;
  /* Fill the auth layout shell (see .layout-login in global styles).
     Do not use min-height: 100vh here — it stacks with layout shells and
     padding-top, causing fake scrollbars (Rows) or a white gap (GT app shell). */
  min-height: 100%;

  /* Signup / invite stepper (ValidateUserForm).
     Finished = filled primary + white check.
     Current: Ant custom icons default to 32px with no circle — that clips.
     Invert via --color-step-current-* (white disc + primary ring/check).
     Fallbacks = GT teal. Rows sets the same tokens with navy. */
  .ant-steps-item-finish .ant-steps-item-icon {
    background-color: var(--color-primary, #41aaaf) !important;
    border-color: var(--color-primary, #41aaaf) !important;
  }

  .ant-steps-item-tail {
    padding: 4px 15px !important;
  }

  .ant-steps-item-finish .ant-steps-item-tail::after {
    background-color: var(--color-primary, #41aaaf) !important;
  }

  .ant-steps-item-tail::after {
    height: 4px !important;
  }

  .ant-steps-item-finish .ant-steps-icon {
    color: #ffffff !important;
  }

  .ant-steps-item-process .ant-steps-item-icon,
  .ant-steps-item-active .ant-steps-item-icon,
  .ant-steps-item-custom.ant-steps-item-process .ant-steps-item-icon,
  .ant-steps-item-custom.ant-steps-item-active .ant-steps-item-icon {
    box-sizing: border-box !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    overflow: hidden !important;
    background-color: var(
      --color-step-current-bg,
      var(--color-surface-raised, #ffffff)
    ) !important;
    border: 1px solid
      var(--color-step-current-border, var(--color-primary, #41aaaf)) !important;
    border-radius: 50% !important;
    width: 32px !important;
    height: 32px !important;
    line-height: 32px !important;
  }

  .ant-steps-item-process .ant-steps-icon,
  .ant-steps-item-active .ant-steps-icon,
  .ant-steps-item-custom .ant-steps-item-icon > .ant-steps-icon {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    line-height: 1 !important;
    font-size: 14px !important;
    color: var(--color-step-current-icon, var(--color-primary, #41aaaf)) !important;
  }

  .ant-steps-item-active .ant-steps-icon .anticon,
  .ant-steps-item-custom .ant-steps-icon .anticon {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    line-height: 0 !important;
    font-size: 14px !important;
    color: inherit !important;
  }

  .ant-steps-item-active .ant-steps-icon .anticon svg,
  .ant-steps-item-custom .ant-steps-icon .anticon svg {
    display: block;
    width: 14px;
    height: 14px;
  }

  .ant-steps-item-finish .ant-steps-item-title {
    font-size: 12px;
    font-weight: 400;
    line-height: 24px;
    color: var(--color-text-muted, #5a6a6a) !important;
  }

  .ant-steps-item-active .ant-steps-item-title {
    font-size: 12px;
    font-weight: 400;
    line-height: 24px;
    color: var(--color-text-dark, #042a2a) !important;
  }

  .link-text {
    color: var(--color-text-muted, #5a6a6a);
    font-family: Inter;
    font-size: 12px;
    font-weight: 400;
    line-height: 24px;
    text-align: left;
  }
`;
export const FormWrapper = styled(PageWrapper)`
  .link-text {
    color: var(--color-text-muted, #5a6a6a);
    font-family: Inter;
    font-size: 12px;
    font-weight: 400;
    line-height: 24px;
    text-align: left;
  }

  .link {
    text-decoration: underline;
    text-underline-offset: 2px;
    color: var(--color-text-muted, #5a6a6a);
  }

  .password-link {
    width: 100%;
    text-align: right;
    min-width: 300px;
    max-width: 330px;
    color: var(--color-text-dark, #042a2a);
    font-weight: 500;
  }

  .sign-up-link {
    font-family: Inter;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    text-align: center;
    color: var(--color-text-dark, #042a2a);
    text-decoration: underline;
    text-underline-offset: 4px;
  }

  .auth-form .ant-form-item-label,
  .login-form .ant-form-item-label {
    padding-bottom: 4px; /* half of Ant Design default (8px) */
  }

  .auth-form .ant-form-item-label > label,
  .login-form .ant-form-item-label > label {
    font-size: 12px;
    font-weight: 400;
    height: auto;
    line-height: 16px;
    color: var(--color-text-muted, #5a6a6a);
  }

  /* Hide visible labels by default; shown when accessibility mode is on */
  .auth-form:not(.auth-form--a11y) .ant-form-item-label,
  .login-form:not(.login-form--a11y) .ant-form-item-label {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .login-a11y-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 12px;
    padding-bottom: 8px;
    font-family: Inter, sans-serif;
    font-size: 11px;
    line-height: 16px;
    color: var(--color-text-muted, #5a6a6a);
  }

  .login-a11y-toggle--header {
    margin-top: 8px;
    margin-bottom: 4px;
    padding-bottom: 0;
    flex-shrink: 0;
  }

  .login-a11y-toggle__label {
    user-select: none;
  }

  .scope-login-mfa {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  .scope-login-mfa__title {
    max-width: 100%;
    text-align: center;
  }

  .scope-login-mfa__back {
    display: block;
    margin-top: 20px;
    text-align: center;
  }
`;

export const LabelWrapper = styled.div.attrs({
  className: 'scope-form-label',
})`
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 5px;
  min-width: 50px;
  font-family: var(--font-family, Inter, sans-serif);
  font-size: var(--font-size-small, 12px);
  font-weight: 400;
  line-height: 18px;
  color: var(--color-text-muted, #5a6a6a) !important;

  .label-text {
    font: inherit;
    color: inherit !important;
  }
`;
